// lib/api.ts — typed fetch wrappers for the Worker API.
// Base URL is `/api` so Vite dev proxy (and the deployed Pages→Worker binding)
// both work without env configuration.

export interface PackQuestion {
  id: string
  type:
    | 'long_text'
    | 'short_text'
    | 'radio'
    | 'checkbox_group'
    | 'select'
    | 'number'
    | 'email'
    | 'url'
    | 'date'
    | 'month'
  prompt: string
  required?: boolean
  hint?: string
  choices?: string[]
  /** Alias for `choices` — packs authored before this field existed used `options`. */
  options?: string[]
  min?: number
  max?: number
  placeholder?: string
}

export interface PackSection {
  key: string
  title: string
  questions: PackQuestion[]
}

export interface PackRow {
  id: string
  slug: string
  title: string
  blurb: string
  voice_note: string
  questions_json: string
  created_at: number
  updated_at: number
}

export interface Pack extends PackRow {
  sections: PackSection[]
  reactive?: {
    enabled?: boolean
    max_followups_per_question?: number
    max_total_followups?: number
    moves?: string[]
  }
  public_share?: boolean
}

export type InvitationStatus =
  | 'unsent'
  | 'sent'
  | 'opened'
  | 'in_progress'
  | 'completed'
  | 'abandoned'

export type ConsentTier = 'anon' | 'first_name_role_city' | 'named_handle'

export interface Invitation {
  id: string
  pack_id: string
  token: string
  label: string | null
  status: InvitationStatus
  created_at: number
  first_opened_at: number | null
  first_answered_at: number | null
  completed_at: number | null
  consent_tier: ConsentTier | null
  respondent_name: string | null
  respondent_role: string | null
  respondent_city: string | null
  respondent_handle: string | null
}

export interface Answer {
  id: string
  invitation_id: string
  question_id: string
  answer_text: string | null
  answer_choice: string | null
  answer_number: number | null
  skipped: 0 | 1
  answered_at: number
}

export interface AdminTag {
  id: string
  answer_id: string
  tag: 'QUOTABLE' | 'BACKGROUND' | 'OMIT'
  chapter_hint: string | null
  paraphrase: string | null
  note: string | null
  created_at: number
}

export interface AdminPackRow {
  id: string
  slug: string
  title: string
  blurb: string
  created_at: number
  updated_at: number
  counts: Record<string, number>
  question_count: number
}

export interface MintedInvitation {
  id: string
  token: string
  label: string | null
  url: string
}

// ---------- internals ----------

class ApiError extends Error {
  status: number
  data: unknown
  constructor(status: number, message: string, data?: unknown) {
    super(message)
    this.status = status
    this.data = data
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'accept': 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
    ...init,
  })
  const ctype = res.headers.get('content-type') ?? ''
  const data = ctype.includes('application/json') ? await res.json() : null
  if (!res.ok) {
    const msg =
      (data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)
        ? String((data as Record<string, unknown>).error)
        : res.statusText) || 'request_failed'
    throw new ApiError(res.status, msg, data)
  }
  return data as T
}

function parsePack(row: PackRow | (PackRow & { sections?: PackSection[] })): Pack {
  // Always parse questions_json so reactive/public_share flags survive even
  // when the server pre-attaches `sections` (translated path).
  let parsed: {
    sections?: PackSection[]
    reactive?: Pack['reactive']
    public_share?: boolean
  } = {}
  try {
    parsed = row.questions_json ? JSON.parse(row.questions_json) : {}
  } catch {
    parsed = {}
  }
  const preAttached = (row as unknown as { sections?: PackSection[] }).sections
  const sections = Array.isArray(preAttached) ? preAttached : parsed.sections ?? []
  return {
    ...row,
    sections,
    reactive: parsed.reactive,
    public_share: parsed.public_share === true,
  }
}

export { ApiError }

// ---------- public (interviewee) ----------

export async function getInvitation(
  token: string,
  lang?: Lang,
): Promise<{
  pack: Pack
  invitation: Invitation
  answers: Answer[]
  lang: Lang
}> {
  const qs = lang && lang !== 'en' ? `?lang=${encodeURIComponent(lang)}` : ''
  const res = await request<{
    pack: PackRow & { sections?: PackSection[] }
    invitation: Invitation
    answers: Answer[]
    lang?: Lang
  }>(`/api/invitations/${encodeURIComponent(token)}${qs}`)
  return {
    pack: parsePack(res.pack),
    invitation: res.invitation,
    answers: res.answers,
    lang: res.lang ?? 'en',
  }
}

export async function saveAnswer(
  token: string,
  payload: {
    question_id: string
    answer_text?: string | null
    answer_choice?: string | null
    answer_number?: number | null
    skipped?: boolean
  },
): Promise<{ answer: Answer }> {
  return request<{ answer: Answer }>(
    `/api/invitations/${encodeURIComponent(token)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  )
}

export async function submitInvitation(
  token: string,
  payload: {
    consent_tier: ConsentTier
    respondent_name?: string | null
    respondent_role?: string | null
    respondent_city?: string | null
    respondent_handle?: string | null
  },
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/invitations/${encodeURIComponent(token)}/submit`,
    { method: 'POST', body: JSON.stringify(payload) },
  )
}

// ---------- admin ----------

export async function adminLogin(token: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/admin/login', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
}

export async function adminLogout(): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/admin/logout', { method: 'POST' })
}

export async function listPacks(): Promise<{ packs: AdminPackRow[] }> {
  return request<{ packs: AdminPackRow[] }>('/api/admin/packs')
}

export async function createInvitations(
  pack_slug: string,
  labels: (string | null)[],
): Promise<{ invitations: MintedInvitation[] }> {
  return request<{ invitations: MintedInvitation[] }>(
    '/api/admin/invitations',
    { method: 'POST', body: JSON.stringify({ pack_slug, labels }) },
  )
}

export async function listPackInvitations(
  slug: string,
): Promise<{
  pack: { id: string; slug: string; title: string; public_share: boolean }
  invitations: Invitation[]
}> {
  return request(`/api/admin/packs/${encodeURIComponent(slug)}/invitations`)
}

export async function getInvitationDetail(id: string): Promise<{
  invitation: Invitation
  pack: Pack
  answers: Answer[]
  admin_tags: AdminTag[]
}> {
  const res = await request<{
    invitation: Invitation
    pack: PackRow
    answers: Answer[]
    admin_tags: AdminTag[]
  }>(`/api/admin/invitations/${encodeURIComponent(id)}`)
  return { ...res, pack: parsePack(res.pack) }
}

export async function tagAnswer(
  answer_id: string,
  payload: {
    tag: 'QUOTABLE' | 'BACKGROUND' | 'OMIT'
    chapter_hint?: string | null
    paraphrase?: string | null
    note?: string | null
  },
): Promise<{ admin_tag: AdminTag }> {
  return request<{ admin_tag: AdminTag }>(
    `/api/admin/answers/${encodeURIComponent(answer_id)}/tag`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  )
}

// CSV download — returns a URL the browser can just navigate to.
export function downloadExport(slug: string, identify: boolean): string {
  const q = identify ? '?identify=1' : '?identify=0'
  return `/api/admin/export/${encodeURIComponent(slug)}.csv${q}`
}

// Single-invitation CSV download URL.
export function downloadInvitationExport(id: string, identify: boolean): string {
  const q = identify ? '?identify=1' : '?identify=0'
  return `/api/admin/invitations/${encodeURIComponent(id)}/export.csv${q}`
}

// All-packs global CSV download URL.
export function downloadGlobalExport(identify: boolean): string {
  const q = identify ? '?identify=1' : '?identify=0'
  return `/api/admin/export/all.csv${q}`
}

// ---------- language support ----------

export const SUPPORTED_LANGS = ['en', 'pl', 'uk', 'ru', 'es'] as const
export type Lang = (typeof SUPPORTED_LANGS)[number]

export const LANG_LABELS: Record<Lang, string> = {
  en: 'English',
  pl: 'Polski',
  uk: 'Українська',
  ru: 'Русский',
  es: 'Español',
}

const LANG_STORAGE_KEY = 'bi:lang'

export function getStoredLang(): Lang {
  if (typeof window === 'undefined') return 'en'
  const v = window.localStorage.getItem(LANG_STORAGE_KEY)
  if (v && (SUPPORTED_LANGS as readonly string[]).includes(v)) return v as Lang
  // Best-effort auto-detect from browser language.
  const nav = window.navigator?.language?.slice(0, 2).toLowerCase() ?? ''
  if ((SUPPORTED_LANGS as readonly string[]).includes(nav)) return nav as Lang
  return 'en'
}

export function setStoredLang(lang: Lang): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(LANG_STORAGE_KEY, lang)
}

// ---------- reactive follow-ups ----------

export interface ReactiveFollowup {
  id: string
  move: 'specificity' | 'clarification' | 'contradiction' | 'echo' | 'bridge'
  prompt: string
  move_reasoning: string
}

export async function fetchReactiveFollowups(
  token: string,
  question_id: string,
  answer_text: string,
  lang?: Lang,
): Promise<{
  followups: ReactiveFollowup[]
  decision_reasoning: string
  skipped: boolean
}> {
  return request(
    `/api/invitations/${encodeURIComponent(token)}/react`,
    {
      method: 'POST',
      body: JSON.stringify({
        question_id,
        answer_text,
        lang: lang ?? getStoredLang(),
      }),
    },
  )
}

// ---------- triage + signals ----------

export interface TriageQueueItem {
  answer_id: string
  invitation_id: string
  pack_slug: string
  pack_title: string
  question_id: string
  section: string
  prompt: string
  answer_text: string | null
  answer_choice: string | null
  skipped: 0 | 1
  answered_at: number
  current_tag: 'QUOTABLE' | 'BACKGROUND' | 'OMIT' | null
  current_chapter_hint: string | null
  current_paraphrase: string | null
  current_note: string | null
  invitation_label: string | null
  consent_tier: string | null
}

export async function listTriageQueue(params: {
  pack?: string
  status?: 'untagged' | 'tagged' | 'all'
  limit?: number
  offset?: number
}): Promise<{ queue: TriageQueueItem[]; total: number; remaining: number }> {
  const qs = new URLSearchParams()
  if (params.pack) qs.set('pack', params.pack)
  if (params.status) qs.set('status', params.status)
  if (params.limit != null) qs.set('limit', String(params.limit))
  if (params.offset != null) qs.set('offset', String(params.offset))
  return request(`/api/admin/triage/queue?${qs.toString()}`)
}

export async function saveTriageTag(payload: {
  answer_id: string
  tag: 'QUOTABLE' | 'BACKGROUND' | 'OMIT' | null
  chapter_hint?: string | null
  paraphrase?: string | null
  note?: string | null
}): Promise<{ ok: true; admin_tag: AdminTag | null }> {
  return request<{ ok: true; admin_tag: AdminTag | null }>(
    '/api/admin/triage/tag',
    { method: 'POST', body: JSON.stringify(payload) },
  )
}

export interface SignalsPackRow {
  slug: string
  title: string
  total: number
  completed: number
  opened: number
  in_progress: number
  mean_answer_words: number
  abandoned_over_24h: number
  completion_rate: number
}

export interface SignalsTotals {
  submissions_7d: number
  submissions_30d: number
  mean_answer_words: number
  quotable_tagged: number
  completed_total: number
}

export interface SignalsTimelinePoint {
  day: string
  submissions: number
  completions: number
}

export async function getSignalsOverview(): Promise<{
  packs: SignalsPackRow[]
  totals: SignalsTotals
  timeline: SignalsTimelinePoint[]
}> {
  return request('/api/admin/signals/overview')
}

export async function deleteInvitation(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/admin/invitations/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  )
}
